import { useDispatch } from "react-redux";
import {
  setDashboardData,
  setDashboardError,
  setDashboardLoading,
  setProjectDashboardData,
  setProjectDashboardError,
  setProjectDashboardLoading,
  setRefreshStatus,
  setRefreshStatusError,
  setRefreshStatusLoading,
  setRoadmapDashboardData,
  setRoadmapDashboardError,
  setRoadmapDashboardLoading,
  setRoadmapGroupDashboardData,
  setRoadmapGroupDashboardError,
  setRoadmapGroupDashboardLoading,
} from "../redux/slices/dashboard";
import axios from "../utils/axiosConfig";
import { errorHandler } from "../utils/errorHandler";

const useDashboardService = () => {
  const dispatch = useDispatch();

  const showError = (error: any) => {
    errorHandler.showError(error);
  };

  // Get Organization Dashboard
  const fetchDashboard = async () => {
    dispatch(setDashboardLoading(true));
    try {
      const response = await axios.get("/dashboard");
      dispatch(setDashboardData(response.data));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setDashboardError(error));
    } finally {
      dispatch(setDashboardLoading(false));
    }
  };

  // Get Project Dashboard
  const fetchProjectDashboard = async (
    projectId: string,
    simFlag: boolean = false,
  ) => {
    dispatch(setProjectDashboardLoading(true));
    try {
      const response = await axios.get(
        `/dashboard/project/${projectId}?sim_flag=${simFlag}`,
      );
      dispatch(setProjectDashboardData(response.data));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setProjectDashboardError(error));
    } finally {
      dispatch(setProjectDashboardLoading(false));
    }
  };

  // Get Roadmap Dashboard
  const fetchRoadmapDashboard = async (roadmapId: string) => {
    dispatch(setRoadmapDashboardLoading(true));
    try {
      const response = await axios.get(`/dashboard/roadmap/${roadmapId}`);
      dispatch(setRoadmapDashboardData(response.data));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setRoadmapDashboardError(error));
    } finally {
      dispatch(setRoadmapDashboardLoading(false));
    }
  };

  // Get Roadmap Group Dashboard
  const fetchRoadmapGroupDashboard = async (groupId: string) => {
    dispatch(setRoadmapGroupDashboardLoading(true));
    try {
      const response = await axios.get(`/dashboard/roadmap-group/${groupId}`);
      dispatch(setRoadmapGroupDashboardData(response.data));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setRoadmapGroupDashboardError(error));
    } finally {
      dispatch(setRoadmapGroupDashboardLoading(false));
    }
  };

  // Get Refresh Status for an entity (project, roadmap, or roadmap group)
  const checkRefreshStatus = async (entityId: string) => {
    dispatch(setRefreshStatusLoading(true));
    try {
      const response = await axios.get(`/dashboard/refresh/status/${entityId}`);
      dispatch(setRefreshStatus({ [entityId]: response.data }));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setRefreshStatusError(error));
    } finally {
      dispatch(setRefreshStatusLoading(false));
    }
  };

  // Force refresh a roadmap group
  const refreshRoadmapGroup = async (groupId: string) => {
    dispatch(setRoadmapGroupDashboardLoading(true));
    try {
      const response = await axios.get(`/dashboard/roadmap-group/${groupId}`, {
        params: { force_refresh: true },
      });
      dispatch(setRoadmapGroupDashboardData(response.data));
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setRoadmapGroupDashboardError(error));
    } finally {
      dispatch(setRoadmapGroupDashboardLoading(false));
    }
  };

  return {
    fetchDashboard,
    fetchProjectDashboard,
    fetchRoadmapDashboard,
    fetchRoadmapGroupDashboard,
    checkRefreshStatus,
    refreshRoadmapGroup,
  };
};

export default useDashboardService;
export { useDashboardService };
